import reactLazyWithRetry from '@fatso83/retry-dynamic-import/react-lazy';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { grey } from '@mui/material/colors';
import { darken } from '@mui/system';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { createAppTheme } from '@parkdna/ui/theme';
import { useThemeNameStore } from '@parkdna/ui/theme/themeStore';
import { ConfigProvider as AntdConfigProvider } from 'antd';
import { ConfirmProvider } from 'material-ui-confirm';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { Suspense, useEffect, useState } from 'react';

import { confirmDefaultOptions } from '@/components/confirmDefaultOptions';
import { CurrentUserTimezoneProvider } from '@/components/CurrentUserTimezoneProvider';
import { SnackbarProvider } from '@/contexts/snackbar';
import { ApiClientProvider, ReactQueryClientProvider } from '@/lib/api';

const ReactQueryDevtoolsProduction = reactLazyWithRetry(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const inputAutofillCSSSelector = `
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active
` as const;

export default function Providers({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [showDevtools, setShowDevtools] = useState(false);
  const themeName = useThemeNameStore((state) => state.themeName);
  const theme = createAppTheme(themeName);

  useEffect(() => {
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          '.MuiButton-root[data-loading="true"]': {
            backgroundColor: darken(theme.palette.primary.main, 0.4),
            cursor: 'wait',
          },
          '.MuiButton-root.Mui-disabled': {
            color:
              theme.palette.mode === 'dark'
                ? `${grey[500]} !important`
                : undefined,
          },
          [inputAutofillCSSSelector]: {
            // Fix input styles on autofill in webkit based browsers
            // https://stackoverflow.com/questions/2781549/removing-input-background-colour-for-chrome-autocomplete
            '-webkit-background-clip': 'text',
            '-webkit-text-fill-color': theme.palette.text.primary,
          },
          ':root': {
            // https://mui.com/x/react-data-grid/layout/#overlay-height
            '--DataGrid-overlayHeight': '350px',
            '--fc-button-bg-color': theme.palette.primary.main,
            '--fc-button-text-color': theme.palette.text.primary,
            '--fc-button-border-color': theme.palette.primary.main,
            '--fc-button-hover-bg-color': theme.palette.action.hover,
            '--fc-button-active-bg-color': theme.palette.action.active,
            '--fc-button-border-radius': `${theme.shape.borderRadius}px`,
            '--fc-button-padding': `${theme.spacing(1)} ${theme.spacing(2)}`,
            '--fc-page-background-color': theme.palette.background.default,
            '--fc-today-bg-color': theme.palette.action.selected,
            '--fc-today-border-color': theme.palette.action.selected,
            '--fc-neutral-text-color': theme.palette.text.primary,
            '--fc-neutral-bg-color': theme.palette.background.paper,
            '--fc-list-event-hover-bg-color':
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
          },
          '.Mui-focusVisible': {
            boxShadow: `0 0 0 2px ${theme.palette.grey['200']} !important`,
          },
        }}
      />
      <SnackbarProvider
        maxSnack={3}
        preventDuplicate
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <AntdConfigProvider
          theme={{
            token: {
              colorPrimary: theme.palette.primary.main,
              colorSuccess: theme.palette.success.main,
              colorWarning: theme.palette.warning.main,
              colorError: theme.palette.error.main,
              colorInfo: theme.palette.info.main,

              colorTextBase: theme.palette.text.primary,
              colorText: theme.palette.text.primary,
              colorTextSecondary: theme.palette.text.secondary,
              colorBgBase: theme.palette.background.default,
              colorBgContainer: theme.palette.background.paper,

              // Typography
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.fontSize,

              // Border
              borderRadius: theme.shape.borderRadius,

              // Shadows (pick one MUI shadow or use custom)
              boxShadow: theme.shadows[1],

              controlItemBgActive: theme.palette.action.active,
            },
            components: {
              DatePicker: {
                activeBorderColor: theme.palette.action.active,
                hoverBorderColor: theme.palette.action.hover,
                cellActiveWithRangeBg: theme.palette.action.active,
                cellHeight: 30,
                cellHoverBg: theme.palette.action.hover,
                cellRangeBorderColor: theme.palette.primary.main,
                cellHoverWithRangeBg: theme.palette.primary.main,
                zIndexPopup: theme.zIndex.modal,
              },
            },
          }}
        >
          <ApiClientProvider>
            <ReactQueryClientProvider>
              <ConfirmProvider defaultOptions={confirmDefaultOptions}>
                <NuqsAdapter>
                  <LocalizationProvider
                    dateAdapter={AdapterLuxon}
                    adapterLocale="en-us"
                  >
                    <CssBaseline />
                    <CurrentUserTimezoneProvider>
                      {children}
                    </CurrentUserTimezoneProvider>
                    {showDevtools && (
                      <Suspense fallback={null}>
                        <ReactQueryDevtoolsProduction />
                      </Suspense>
                    )}
                  </LocalizationProvider>
                </NuqsAdapter>
              </ConfirmProvider>
            </ReactQueryClientProvider>
          </ApiClientProvider>
        </AntdConfigProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
